import { useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { updateDomain } from '@/services/domainRegistry';
import { getCentralDomainDefinition } from '@/services/domain/centralDefinitions';
import {
  canConfigureDomain,
  getDomainConfigurationPathState,
} from '@/services/domain/centralModel';

function pathValue(source, path) {
  return path.split('.').reduce((value, part) => value?.[part], source);
}

function hasPath(source, path) {
  const parts = path.split('.');
  let cursor = source;
  for (const part of parts) {
    if (!cursor || !Object.prototype.hasOwnProperty.call(cursor, part)) return false;
    cursor = cursor[part];
  }
  return true;
}

function FieldState({ domain, path, resetPaths, canReset, onReset, busy }) {
  const state = getDomainConfigurationPathState(domain, 'overview', path);
  const configured = hasPath(domain.governance?.configuration?.overview, path)
    && !resetPaths.has(path);
  if (state?.configurable && configured) {
    return (
      <span className="flex items-center gap-1.5">
        <span className="badge badge-primary badge-outline badge-sm">School override</span>
        {canReset && (
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1"
            onClick={() => onReset(path)}
            disabled={busy}
          >
            <ArrowPathIcon className="h-3.5 w-3.5" /> Reset
          </button>
        )}
      </span>
    );
  }
  if (state?.configurable) {
    return <span className="badge badge-ghost badge-sm">Swan default</span>;
  }
  return (
    <span className="badge badge-ghost badge-sm gap-1" title={state?.reason || undefined}>
      <LockClosedIcon className="h-3 w-3" /> Locked
    </span>
  );
}

function LockReason({ domain, path }) {
  const state = getDomainConfigurationPathState(domain, 'overview', path);
  if (state?.configurable || !state?.reason) return null;
  return <p className="text-xs text-base-content/45">{state.reason}</p>;
}

/**
 * Domain identity editor. Event fields deliberately live on the Event tab;
 * this form is limited to policy-permitted identity, terminology, and scope.
 */
export function DomainEditForm({ domain, schoolId, onSuccess, onCancel }) {
  const [label, setLabel] = useState(domain.label || '');
  const [description, setDescription] = useState(domain.description || '');
  const [eventLabel, setEventLabel] = useState(domain.terminology?.event?.label || 'event');
  const [eventPlural, setEventPlural] = useState(domain.terminology?.event?.plural || 'events');
  const [actionLabel, setActionLabel] = useState(domain.terminology?.action?.label || 'action');
  const [actionPlural, setActionPlural] = useState(domain.terminology?.action?.plural || 'actions');
  const [scopeGrades, setScopeGrades] = useState((domain.scope?.grades || []).join(', '));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle');
  const [resetPaths, setResetPaths] = useState(new Set());

  const definition = getCentralDomainDefinition(
    domain.governance?.definitionId || domain.governance?.prototypeId || domain.name,
  );
  const inherited = definition?.config || null;

  const canEdit = (path) => canConfigureDomain(domain, 'overview', 'edit', path);
  const canEditLabel = canEdit('label');
  const canEditDescription = canEdit('description');
  const canEditTerminology = canEdit('terminology');
  const canEditScope = canEdit('scope');
  const hasEditableField = canEditLabel || canEditDescription || canEditTerminology || canEditScope;

  const applyLocalValue = (path, value) => {
    if (path === 'label') setLabel(value || '');
    if (path === 'description') setDescription(value || '');
    if (path === 'scope') setScopeGrades((value?.grades || []).join(', '));
    if (path === 'terminology') {
      setEventLabel(value?.event?.label || 'event');
      setEventPlural(value?.event?.plural || 'events');
      setActionLabel(value?.action?.label || 'action');
      setActionPlural(value?.action?.plural || 'actions');
    }
  };

  const resetPath = async (path) => {
    const value = pathValue(inherited, path);
    if (value === undefined) return;
    setIsSubmitting(true);
    setStatus('saving');
    setError(null);
    try {
      await updateDomain(schoolId, domain.id, { [path]: value });
      applyLocalValue(path, value);
      setResetPaths((current) => new Set([...current, path]));
      setStatus('saved');
    } catch (resetError) {
      setError(resetError.message || 'Failed to reset this setting.');
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stateProps = (path) => ({
    domain,
    path,
    resetPaths,
    canReset: pathValue(inherited, path) !== undefined
      && !(domain.governance?.definitionAuthority === 'school' && path === 'label'),
    onReset: resetPath,
    busy: isSubmitting,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canEditLabel && !label.trim()) {
      setError('Name is required.');
      return;
    }

    const updates = {};
    if (canEditLabel) updates.label = label.trim();
    if (canEditDescription) updates.description = description.trim();
    if (canEditScope) {
      updates.scope = {
        ...(domain.scope || {}),
        grades: scopeGrades.split(',').map((grade) => grade.trim()).filter(Boolean),
      };
    }
    if (canEditTerminology) {
      updates.terminology = {
        ...(domain.terminology || {}),
        event: {
          ...(domain.terminology?.event || {}),
          label: eventLabel.trim() || 'event',
          plural: eventPlural.trim() || 'events',
        },
        action: {
          ...(domain.terminology?.action || {}),
          label: actionLabel.trim() || 'action',
          plural: actionPlural.trim() || 'actions',
        },
      };
    }

    if (!Object.keys(updates).length) {
      setError('This Domain Standard has no school-configurable identity fields.');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    setStatus('saving');
    try {
      await updateDomain(schoolId, domain.id, updates);
      setStatus('saved');
      window.setTimeout(() => onSuccess?.(), 900);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save changes.');
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex min-h-6 items-center justify-end">
        {status === 'saving' && <span className="badge badge-info badge-outline">Saving…</span>}
        {status === 'saved' && <span className="badge badge-success badge-outline gap-1"><CheckCircleIcon className="h-3.5 w-3.5" /> Saved</span>}
      </div>
      {error && <div className="alert alert-error py-3 text-sm" role="alert"><span>{error}</span></div>}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor="domain-display-name" className="text-sm font-medium">Name</label>
          <FieldState {...stateProps('label')} />
        </div>
        <input
          id="domain-display-name"
          type="text"
          className="input input-bordered w-full disabled:border-base-300 disabled:bg-base-200/60 disabled:text-base-content/65"
          aria-label="Name"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          disabled={!canEditLabel || isSubmitting}
          required={canEditLabel}
        />
        <LockReason domain={domain} path="label" />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor="domain-description" className="text-sm font-medium">Description</label>
          <FieldState {...stateProps('description')} />
        </div>
        <textarea
          id="domain-description"
          className="textarea textarea-bordered min-h-20 w-full resize-none disabled:border-base-300 disabled:bg-base-200/60 disabled:text-base-content/65"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={!canEditDescription || isSubmitting}
        />
        <LockReason domain={domain} path="description" />
      </div>

      <fieldset className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <legend className="text-sm font-semibold">Words used in this Domain</legend>
          <FieldState {...stateProps('terminology')} />
        </div>
        <p className="text-xs text-base-content/55">
          These labels appear in recording and follow-up screens. Event fields themselves stay under Event.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="domain-event-singular" className="text-xs font-medium">Recorded item — singular</label>
            <input id="domain-event-singular" className="input input-bordered input-sm w-full" value={eventLabel} onChange={(event) => setEventLabel(event.target.value)} disabled={!canEditTerminology || isSubmitting} placeholder="event" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="domain-event-plural" className="text-xs font-medium">Recorded item — plural</label>
            <input id="domain-event-plural" className="input input-bordered input-sm w-full" value={eventPlural} onChange={(event) => setEventPlural(event.target.value)} disabled={!canEditTerminology || isSubmitting} placeholder="events" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="domain-action-singular" className="text-xs font-medium">Follow-up — singular</label>
            <input id="domain-action-singular" className="input input-bordered input-sm w-full" value={actionLabel} onChange={(event) => setActionLabel(event.target.value)} disabled={!canEditTerminology || isSubmitting} placeholder="action" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="domain-action-plural" className="text-xs font-medium">Follow-up — plural</label>
            <input id="domain-action-plural" className="input input-bordered input-sm w-full" value={actionPlural} onChange={(event) => setActionPlural(event.target.value)} disabled={!canEditTerminology || isSubmitting} placeholder="actions" />
          </div>
        </div>
        <LockReason domain={domain} path="terminology" />
      </fieldset>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor="domain-visible-grades" className="text-sm font-medium">Visible to grades</label>
          <FieldState {...stateProps('scope')} />
        </div>
        <input
          id="domain-visible-grades"
          type="text"
          className="input input-bordered w-full"
          aria-label="Visible to grades"
          placeholder="e.g. 7, 8, 9 — leave empty for all grades"
          value={scopeGrades}
          onChange={(event) => setScopeGrades(event.target.value)}
          disabled={!canEditScope || isSubmitting}
        />
        <LockReason domain={domain} path="scope" />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={isSubmitting}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting || !hasEditableField || (canEditLabel && !label.trim())}>
          {isSubmitting && <span className="loading loading-spinner loading-sm" />}
          {status === 'error' ? 'Retry save' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export default DomainEditForm;
